class SectionTracking extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.handleClick = this.handleClick.bind(this)

    // Capture phase fires before any element handler, so navigation triggered
    // by a click (e.g. redirect-to-login) can't interrupt tracking
    document.addEventListener('click', this.handleClick, { capture: true })
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    document.removeEventListener('click', this.handleClick, { capture: true })
  }

  /**
   * Handles all document clicks. Walks up from the click target to find both
   * an interactive element and a tracked section ancestor.
   *
   * @param {MouseEvent} event
   */
  handleClick (event) {
    const interactiveEl = event.target.closest(
      'a, button, [role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="option"], [role="switch"], [role="checkbox"], [role="radio"], input, select, textarea, summary',
    )
    if (!interactiveEl) return

    const trackedEl = event.target.closest(`[${this._externalElIdentifier}]`)
    if (!trackedEl) return

    const context = this.getElementContext(trackedEl, interactiveEl)
    if (!context.name) return

    const { name, ...rest } = context

    const allSameType = Array.from(
      document.querySelectorAll(
        `[${this._externalElIdentifier}][data-section-tracking-name="${name}"]`,
      ),
    )
    const sectionIndex = allSameType.indexOf(trackedEl) + 1

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'section_engagement',
      section_name: `${name}-${sectionIndex}`,
      element_name: trackedEl.getAttribute(this._externalElIdentifier),
      ...rest,
    })
  }

  /**
   * Collects contextual data from the clicked element:
   * - link_url: href from the element itself or its nearest <a> ancestor
   * - element_type: type attribute (e.g. 'submit', 'button')
   * - any data-section-tracking-* attributes are extracted and mapped to
   *   snake_case payload keys, e.g.:
   *     data-section-tracking-slide-index="2" -> slide_index: "2"
   *     data-section-tracking-action="next"   -> action: "next"
   *
   * @param {HTMLElement} el
   * @returns {Object}
   */
  getElementContext (el, clickedEl = el) {
    const context = {}

    // Check from the clicked element so we catch links that are descendants of the tracked el
    const linkEl =
      clickedEl.tagName === 'A' ? clickedEl : clickedEl.closest('a')
    if (linkEl?.href) {
      context.link_url = linkEl.href
    }

    if (el.type) {
      context.element_type = el.type
    }

    // Identify the specific element that was clicked
    context.clicked_element =
      clickedEl.getAttribute('is') || clickedEl.tagName.toLowerCase()

    const clickedLabel =
      clickedEl.getAttribute('aria-label') || clickedEl.textContent?.trim()
    if (clickedLabel) {
      context.clicked_label = clickedLabel
    }

    // Extract all data-section-tracking-* attributes as extra context,
    // skipping the external-el identifier itself
    for (const [key, value] of Object.entries(el.dataset)) {
      if (!key.startsWith('sectionTracking')) continue
      if (key === 'sectionTrackingExternalEl') continue

      // sectionTrackingSlideIndex -> slide_index
      const snakeKey = key
        .slice('sectionTracking'.length)
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
        .replace(/^_/, '')

      context[snakeKey] = value
    }

    return context
  }
}

if (!customElements.get('section-tracking')) {
  customElements.define('section-tracking', SectionTracking)
}
